import { PaperPlaneRight } from '@phosphor-icons/react';
import { diffLines } from 'diff';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate as useReactNavigate, useParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { Heading } from 'src/components/Heading';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Textarea } from 'src/components/Textarea';
import { useNavigation } from 'src/contexts/NavigationContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, Subject } from 'src/hooks/usePermissions';

import { cn } from 'src/utils/className';

type TranscriptMessage = {
  speaker: 'HUMAN' | 'BOT';
  message: string;
  timestamp?: string;
};

const parseTranscript = (transcript: string) => {
  if (!transcript) return [];

  const lines = transcript.split('\n').filter((line) => line.trim());
  const messages: Array<TranscriptMessage> = [];

  lines.forEach((line) => {
    const match = line.match(/^\[([^\]]+)\]\s*(HUMAN|BOT):\s*(.+)$/);
    if (match) {
      messages.push({
        timestamp: match[1],
        speaker: match[2] as 'HUMAN' | 'BOT',
        message: match[3].trim(),
      });
    }
  });

  return messages;
};

export const ConversationEditor = () => {
  const { get, patch, post } = useAuthedRequest();
  const navigate = useReactNavigate();
  const { setBlocked } = useNavigation();
  const notification = useNotification();

  const { id: callId } = useParams<{ id: string }>();
  const [prompt, setPrompt] = useState('');
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [agentId, setAgentId] = useState('');
  const [agentLoading, setAgentLoading] = useState(false);
  const [callMessages, setCallMessages] = useState<
    { speaker: 'HUMAN' | 'BOT'; message: string; timestamp?: string }[]
  >([]);
  const [originalCallMessages, setOriginalCallMessages] = useState<
    { speaker: 'HUMAN' | 'BOT'; message: string; timestamp?: string }[]
  >([]);
  const [generatedMessages, setGeneratedMessages] = useState<
    { speaker: 'HUMAN' | 'BOT'; message: string; timestamp?: string }[]
  >([]);
  const [selectedBotMessageIndex, setSelectedBotMessageIndex] = useState<
    number | null
  >(null);
  const [generatingResponse, setGeneratingResponse] = useState(false);
  const [llmProvider, setLlmProvider] = useState<string>('');
  const [llmModel, setLlmModel] = useState<string>('');
  const [userMessage, setUserMessage] = useState<string>('');

  const lastChangeRef = useRef<HTMLDivElement>(null);

  const diffs = useMemo(
    () => diffLines(originalPrompt, prompt),
    [originalPrompt, prompt],
  );

  useEffect(() => {
    if (!callId) {
      notification.error('Call not found');
      navigate('/calls');
      return;
    }

    const fetchCall = async () => {
      // Validate that callId is a valid UUID
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(callId)) {
        return;
      }

      const callRecord = await get(
        `${process.env.REACT_APP_BASE_API_URL}/calls/${callId}`,
      );
      if (callRecord.agent.provider && callRecord.agent.provider !== 'openai') {
        notification.error(
          'Conversation editor is only available for OpenAI agents',
        );
        navigate('/calls');
        return;
      }
      const messages = parseTranscript(callRecord.transcript);
      setLlmProvider(callRecord.agent.provider);
      setLlmModel(callRecord.agent.model);
      setCallMessages(messages);
      setOriginalCallMessages(messages);
      setPrompt(callRecord.agent.prompt.content);
      setOriginalPrompt(callRecord.agent.prompt.content);
      setAgentId(callRecord.agent.id);
    };
    fetchCall();
  }, [callId]);

  useEffect(() => {
    if (selectedBotMessageIndex) {
      const generatedMessageHistory = callMessages.slice(
        0,
        selectedBotMessageIndex,
      );
      handleGenerateResponse(generatedMessageHistory);
    }
  }, [selectedBotMessageIndex]);

  useEffect(() => {
    if (lastChangeRef.current) {
      lastChangeRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [diffs]);

  const handleCallMessageClick = (index: number) => {
    if (index === 0) {
      return;
    } // Skip the first message
    if (callMessages[index]?.speaker !== 'BOT') {
      return;
    }
    setSelectedBotMessageIndex(index);
  };

  const canCallMessageClick = (index: number) => {
    return index > 0 && callMessages[index]?.speaker === 'BOT';
  };

  const shouldHighlightCallMessage = (index: number) => {
    return index === selectedBotMessageIndex;
  };

  const shouldHighlightGeneratedMessage = (index: number) => {
    return index === generatedMessages.length - 1;
  };

  const handleGenerateResponse = async (
    messageHistory: TranscriptMessage[],
  ) => {
    const messages = messageHistory.map((msg) => ({
      text: msg.message,
      role: msg.speaker === 'HUMAN' ? 'user' : 'assistant',
    }));
    setGeneratingResponse(true);
    setGeneratedMessages(messageHistory);
    const response = await post(
      `${process.env.REACT_APP_BASE_API_URL}/prompt-builder/chat`,
      {
        messages,
        prompt,
        provider: llmProvider,
        model: llmModel,
      },
    );
    setGeneratedMessages([
      ...messageHistory,
      { speaker: 'BOT', message: response.text },
    ]);
    setGeneratingResponse(false);
    setSelectedBotMessageIndex(0);
  };

  const saveAgent = async () => {
    setAgentLoading(true);
    const agentResp = await patch(`/agents/${agentId}`, {
      prompt: {
        content: prompt,
      },
    });
    setPrompt(agentResp.prompt.content);
    setOriginalPrompt(agentResp.prompt.content);
    setAgentId(agentResp.id);
    setAgentLoading(false);
    notification.success('Agent prompt updated successfully');
  };

  return (
    <div className="flex-1">
      <Heading
        className="border-b-0"
        title="Conversation Editor"
        subtitle="Examine and edit your agent's conversation behavior"
      >
        <div className="flex gap-2 mt-3">
          <Can operation="update" subject={Subject.AGENTS}>
            <Button
              size="small"
              onClick={saveAgent}
              disabled={prompt === originalPrompt || agentLoading}
            >
              {agentLoading ? 'Loading...' : 'Update Agent'}
            </Button>
          </Can>
        </div>
      </Heading>
      <ContentWrapper>
        <div className="flex flex-col h-screen">
          {/* Two ChatBoxes side-by-side */}
          <div className="flex gap-6 mb-4 flex-1 min-h-0">
            <div className="flex flex-col flex-1 min-h-0 basis-1/3">
              <div className="flex justify-between mb-2 items-end">
                <Label>Call Transcript</Label>
                <Button
                  onClick={() => setCallMessages(originalCallMessages)}
                  size="small"
                  color="secondary"
                >
                  Reset
                </Button>
              </div>
              {callMessages.length <= 1 && (
                <div className="flex-1 flex justify-center items-center border border-neutral-200 rounded-lg bg-neutral-50">
                  <div className="text-neutral-500 italic">
                    Please choose a call with human interaction to start
                  </div>
                </div>
              )}
              {callMessages.length > 1 && (
                <ChatBox
                  messages={callMessages}
                  handleMessageClick={handleCallMessageClick}
                  shouldHighlightMessage={shouldHighlightCallMessage}
                  canMessageClick={canCallMessageClick}
                />
              )}
            </div>
            <div className="flex flex-col flex-1 min-h-0 basis-2/3">
              <div className="flex justify-between mb-2 items-end">
                <Label>Simulated Conversation</Label>
                <Button
                  onClick={() => setCallMessages(generatedMessages)}
                  size="small"
                  color="secondary"
                >
                  Use as Transcript
                </Button>
              </div>
              {generatedMessages.length > 0 && (
                <ChatBox
                  messages={
                    generatingResponse
                      ? [
                          ...generatedMessages,
                          { speaker: 'BOT', message: '...' },
                        ]
                      : generatedMessages
                  }
                  shouldHighlightMessage={shouldHighlightGeneratedMessage}
                  autoScroll
                />
              )}
              {generatedMessages.length === 0 && !generatingResponse && (
                <div className="flex-1 flex justify-center items-center border border-neutral-200 rounded-lg bg-neutral-50">
                  <div className="text-neutral-500 italic">
                    Get started by clicking on a bot message in the call
                    transcript
                  </div>
                </div>
              )}
              <Input
                className="mt-2"
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                placeholder="Type a message to continue the conversation"
                icon={<PaperPlaneRight size={20} />}
                inputAction={() => {
                  if (userMessage.trim() === '' || generatingResponse) {
                    return;
                  }
                  handleGenerateResponse([
                    ...generatedMessages,
                    { speaker: 'HUMAN', message: userMessage },
                  ]);
                  setUserMessage('');
                }}
                disabled={generatedMessages.length === 0}
              />
            </div>
          </div>

          {/* Textarea underneath */}
          <div>
            <Label>Agent Prompt</Label>
            <Textarea
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
                if (e.target.value !== originalPrompt) {
                  setBlocked(true);
                } else {
                  setBlocked(false);
                }
              }}
              disabled={callMessages.length === 0}
              placeholder="Will be automatically populated with your call agent prompt"
              rows={10}
            />
          </div>
        </div>
        <div>
          <Label>Change Summary</Label>
          <div className="h-64 overflow-y-auto border border-neutral-300 rounded-md p-3 bg-neutral-50 resize-none">
            {/* Reuse lastChangeRef to scroll to the last change */}
            {diffs.map((part) => {
              if (part.added) {
                return (
                  <div
                    ref={lastChangeRef}
                    className="bg-success-200 whitespace-pre-wrap"
                    key={part.value}
                  >
                    {part.value}
                  </div>
                );
              } else if (part.removed) {
                return (
                  <div
                    ref={lastChangeRef}
                    className="bg-error-200 whitespace-pre-wrap"
                    key={part.value}
                  >
                    {part.value}
                  </div>
                );
              } else {
                return (
                  <div className="whitespace-pre-wrap" key={part.value}>
                    {part.value}
                  </div>
                );
              }
            })}
          </div>
        </div>
      </ContentWrapper>
    </div>
  );
};

type ChatBoxProps = {
  messages: TranscriptMessage[];
  handleMessageClick?: (index: number) => void;
  canMessageClick?: (index: number) => boolean;
  shouldHighlightMessage?: (index: number) => boolean;
  autoScroll?: boolean;
};

const ChatBox = (props: ChatBoxProps) => {
  const {
    messages,
    handleMessageClick,
    canMessageClick,
    shouldHighlightMessage,
    autoScroll,
  } = props;

  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesContainerRef.current && autoScroll) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, autoScroll]);

  return (
    <div className="flex-1 overflow-hidden border border-neutral-200 rounded-lg bg-neutral-50">
      <div
        ref={messagesContainerRef}
        className="h-full space-y-4 overflow-y-auto py-4"
      >
        {messages.map((msg, index) => {
          const isHuman = msg.speaker === 'HUMAN';

          return (
            <div
              key={index}
              id={`message-${index}`}
              className={`flex ${isHuman ? 'justify-end' : 'justify-start'} px-2`}
            >
              <div
                onClick={() => handleMessageClick?.(index)}
                className={cn(
                  `max-w-[80%] rounded-lg px-4 py-2 ${
                    isHuman
                      ? 'bg-primary text-neutral-0'
                      : 'bg-neutral-0 text-neutral-800 border border-neutral-200'
                  } ${canMessageClick?.(index) ? 'cursor-pointer transition-opacity hover:opacity-80' : 'cursor-default'}`,
                  shouldHighlightMessage?.(index)
                    ? `border-2 shadow-lg border-gold-300`
                    : '',
                )}
              >
                <div className="text-xs opacity-70 mb-1">
                  {msg.timestamp ? `${msg.timestamp} - ` : ''}
                  {msg.speaker}
                </div>
                <div className="whitespace-pre-wrap">{msg.message}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
